import React from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Clock,
  Calendar,
  TrendingUp,
  MessageSquare,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  PhoneCall,
  Mail as MailIcon
} from 'lucide-react';
import UsageWidget from './UsageWidget';

const statusConfig = {
  new: { label: 'New', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  contacted: { label: 'Contacted', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  booked: { label: 'Booked', color: 'text-green-400', bg: 'bg-green-500/10' },
  closed: { label: 'Closed', color: 'text-slate-400', bg: 'bg-slate-500/10' },
  lost: { label: 'Lost', color: 'text-red-400', bg: 'bg-red-500/10' }
};

const inquiryConfig = {
  emergency: { label: 'Emergency', color: 'text-red-400', icon: AlertCircle },
  routine: { label: 'Routine', color: 'text-blue-400', icon: Clock },
  estimate: { label: 'Estimate', color: 'text-green-400', icon: Calendar }
};

export default function LeadsTab({ 
  stats, 
  leads, 
  client, 
  navigate, 
  formatDate 
}) {
  return (
    <>
      {/* Stats Grid - First row: 4 stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-sky-400" />
            <span className="text-slate-400 text-sm">Total Leads</span>
          </div>
          <div className="text-3xl font-bold">{stats?.total_leads || 0}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-yellow-400" />
            <span className="text-slate-400 text-sm">This Week</span>
          </div>
          <div className="text-3xl font-bold text-yellow-400">{stats?.leads_this_week || 0}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-green-400" />
            <span className="text-slate-400 text-sm">Booked</span>
          </div>
          <div className="text-3xl font-bold text-green-400">{stats?.appointments_booked || 0}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            <span className="text-slate-400 text-sm">Conversion</span>
          </div>
          <div className="text-3xl font-bold text-purple-400">{stats?.conversion_rate || 0}%</div>
        </motion.div>
      </div>

      {/* Second row: Usage Widget */}
      <div className="mb-8">
        <UsageWidget />
      </div>

      {/* Leads List */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-sky-400" />
            Recent Leads
          </h2>
        </div>

        {leads.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-400 mb-2">No leads yet</h3>
            <p className="text-slate-500 text-sm">
              When customers reach out, their inquiries will appear here.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {leads.map((lead, index) => {
              const status = statusConfig[lead.status] || statusConfig.new;
              const inquiry = inquiryConfig[lead.inquiry_type] || inquiryConfig.routine;
              const InquiryIcon = inquiry.icon;

              return (
                <motion.div
                  key={lead.lead_id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/client/leads/${lead.lead_id}`)}
                  className="px-6 py-4 hover:bg-slate-800/50 cursor-pointer transition-colors group"
                  data-testid={`lead-row-${lead.lead_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full ${inquiry.color === 'text-red-400' ? 'bg-red-500/10' : 'bg-slate-800'} flex items-center justify-center`}>
                        <InquiryIcon className={`w-5 h-5 ${inquiry.color}`} />
                      </div>
                      <div>
                        <div className="font-medium group-hover:text-sky-400 transition-colors">
                          {lead.name}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <PhoneCall className="w-3 h-3" />
                            {lead.phone}
                          </span>
                          {lead.email && (
                            <span className="flex items-center gap-1">
                              <MailIcon className="w-3 h-3" />
                              {lead.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <div className={`text-sm ${inquiry.color}`}>{inquiry.label}</div>
                        <div className="text-xs text-slate-500">{formatDate(lead.created_at)}</div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                        {status.label}
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-sky-400 transition-colors" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* AI Status Banner */}
      <div className="mt-8 bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-3 h-3 rounded-full ${client?.status === 'active' ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse`} />
            <div>
              <div className="font-medium">AI Assistant Status</div>
              <div className="text-sm text-slate-400">
                {client?.status === 'active' 
                  ? 'Your AI is actively responding to inquiries 24/7'
                  : 'Your AI is being configured'}
              </div>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-lg ${client?.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
            {client?.status === 'active' ? (
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Active
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Setting Up
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
